<template>
  <div class="upgrade-options">
    <!-- Billing Toggle -->
    <div v-if="showFoundationPlan || showFluencyPlan" class="billing-toggle">
      <button :class="['toggle-btn', { active: selectedBillingCycle === 'monthly' }]"
        @click="selectedBillingCycle = 'monthly'">
        {{ $t('pricing.monthly') }}
      </button>
      <button :class="['toggle-btn', { active: selectedBillingCycle === 'yearly' }]"
        @click="selectedBillingCycle = 'yearly'">
        {{ $t('pricing.yearly') }}
      </button>
    </div>

    <div class="plans-container">
      <!-- Foundation Plan Card -->
      <div v-if="showFoundationPlan" class="plan-card foundation">
        <h3>{{ $t(pricingStore.plans.foundation.nameKey) }}</h3>
        <div class="price-section">
          <transition name="price" mode="out-in">
            <div class="price-wrapper" :key="selectedBillingCycle">
              <div class="price">
                ${{ selectedBillingCycle === 'monthly'
                  ? pricingStore.plans.foundation.monthlyPrice
                  : pricingStore.plans.foundation.yearlyPrice }}
              </div>
              <div class="per-month">{{ $t('pricing.perMonth') }}</div>
              <div v-if="selectedBillingCycle === 'yearly'" class="yearly-savings">
                <div class="billed-annually">
                  {{ $t('pricing.billedAnnuallyAt', { amount: pricingStore.plans.foundation.yearlyTotal }) }}
                </div>
                <div class="months-free">
                  <span class="highlight">{{ $t('pricing.monthsFree', { count: pricingStore.plans.foundation.yearlyMonthsFree }) }}</span>
                  <span class="savings-badge">
                    {{ $t('pricing.savePercent', { percent: Math.round((pricingStore.plans.foundation.yearlyMonthsFree / 12) * 100) }) }}
                  </span>
                </div>
              </div>
            </div>
          </transition>
        </div>
        <ul class="plan-features-list">
          <li>{{ $t('pricing.chatsPerMonth', { count: pricingStore.plans.foundation.chatsPerMonth }) }}</li>
          <li>{{ $t('pricing.lessonsPerMonth', { count: pricingStore.plans.foundation.lessonsPerMonth }) }}</li>
        </ul>
        <!-- Primary button: Stripe or IAP depending on strategy -->
        <button v-if="paymentStrategy !== 'iap-only'"
          @click="requestUpgrade(selectedBillingCycle === 'monthly' ? 'foundation-month' : 'foundation-year')"
          class="upgrade-btn" :disabled="isProcessing['foundation']">
          <span v-if="!isProcessing['foundation']">{{ $t('pricing.chooseFoundation') }}</span>
          <span v-else class="loading-text">
            <font-awesome-icon icon="spinner" spin />
            {{ $t('pricing.processing') }}
          </span>
        </button>
        <!-- IAP-only button -->
        <button v-if="paymentStrategy === 'iap-only'"
          @click="purchaseIap(selectedBillingCycle === 'monthly' ? 'foundation-monthly' : 'foundation-yearly', 'foundation')"
          class="upgrade-btn" :disabled="isProcessing['foundation']">
          <span v-if="!isProcessing['foundation']">
            {{ $t('pricing.chooseFoundation') }}
            <span v-if="iapPrice('foundation')" class="iap-price">({{ iapPrice('foundation') }})</span>
          </span>
          <span v-else class="loading-text">
            <font-awesome-icon icon="spinner" spin />
            {{ $t('pricing.processing') }}
          </span>
        </button>
        <!-- Secondary IAP button for stripe-preferred regions -->
        <button v-if="paymentStrategy === 'stripe-preferred'"
          @click="purchaseIap(selectedBillingCycle === 'monthly' ? 'foundation-monthly' : 'foundation-yearly', 'foundation')"
          class="upgrade-btn-secondary" :disabled="isProcessing['foundation']">
          {{ $t('pricing.payWithStore', { store: storeName }, `Or pay with ${storeName}`) }}
          <span v-if="iapPrice('foundation')" class="iap-price">({{ iapPrice('foundation') }})</span>
        </button>
        <p v-if="paymentStrategy === 'stripe-preferred'" class="stripe-savings-note">
          {{ $t('pricing.stripeSavingsNote', 'Subscribe directly to save — no app store markup') }}
        </p>
      </div>

      <!-- Fluency Plan Card -->
      <div v-if="showFluencyPlan" class="plan-card fluency">
        <!-- Conditional styling for Foundation users upgrading -->
        <div v-if="currentPlanId === 'foundation'" class="upgrade-highlight-badge">{{ $t('pricing.upgrade') }}</div>
        <h3>{{ $t(pricingStore.plans.fluency.nameKey) }}</h3>
        <div class="price-section">
          <transition name="price" mode="out-in">
            <div class="price-wrapper" :key="selectedBillingCycle">
              <div class="price">
                ${{ selectedBillingCycle === 'monthly'
                  ? pricingStore.plans.fluency.monthlyPrice
                  : pricingStore.plans.fluency.yearlyPrice }}
              </div>
              <div class="per-month">{{ $t('pricing.perMonth') }}</div>
              <div v-if="selectedBillingCycle === 'yearly'" class="yearly-savings">
                <div class="billed-annually">
                  {{ $t('pricing.billedAnnuallyAt', { amount: pricingStore.plans.fluency.yearlyTotal }) }}
                </div>
                <div class="months-free">
                  <span class="highlight">{{ $t('pricing.monthsFree', { count: pricingStore.plans.fluency.yearlyMonthsFree }) }}</span>
                  <span class="savings-badge">
                    {{ $t('pricing.savePercent', { percent: Math.round((pricingStore.plans.fluency.yearlyMonthsFree / 12) * 100) }) }}
                  </span>
                </div>
              </div>
            </div>
          </transition>
        </div>
        <ul class="plan-features-list">
          <li>{{ $t('pricing.chatsPerMonth', { count: pricingStore.plans.fluency.chatsPerMonth }) }}</li>
          <li>{{ $t('pricing.lessonsPerMonth', { count: pricingStore.plans.fluency.lessonsPerMonth }) }}</li>
          <li v-if="pricingStore.plans.fluency.multiLingual">{{ $t('pricing.multiLingualAccess') }}</li>
        </ul>
        <!-- Free users (not locked to a provider): region-based options. -->
        <template v-if="!providerView.isLocked">
          <!-- Primary button: Stripe or IAP depending on strategy -->
          <button v-if="paymentStrategy !== 'iap-only'"
            @click="requestUpgrade(selectedBillingCycle === 'monthly' ? 'fluency-month' : 'fluency-year')"
            class="upgrade-btn" :disabled="isProcessing['fluency']">
            <span v-if="!isProcessing['fluency']">{{ $t('pricing.chooseFluency') }}</span>
            <span v-else class="loading-text">
              <font-awesome-icon icon="spinner" spin />
              {{ $t('pricing.processing') }}
            </span>
          </button>
          <!-- IAP-only button -->
          <button v-if="paymentStrategy === 'iap-only'"
            @click="purchaseIap(selectedBillingCycle === 'monthly' ? 'fluency-monthly' : 'fluency-yearly', 'fluency')"
            class="upgrade-btn" :disabled="isProcessing['fluency']">
            <span v-if="!isProcessing['fluency']">
              {{ $t('pricing.chooseFluency') }}
              <span v-if="iapPrice('fluency')" class="iap-price">({{ iapPrice('fluency') }})</span>
            </span>
            <span v-else class="loading-text">
              <font-awesome-icon icon="spinner" spin />
              {{ $t('pricing.processing') }}
            </span>
          </button>
          <!-- Secondary IAP button for stripe-preferred regions -->
          <button v-if="paymentStrategy === 'stripe-preferred'"
            @click="purchaseIap(selectedBillingCycle === 'monthly' ? 'fluency-monthly' : 'fluency-yearly', 'fluency')"
            class="upgrade-btn-secondary" :disabled="isProcessing['fluency']">
            {{ $t('pricing.payWithStore', { store: storeName }, `Or pay with ${storeName}`) }}
            <span v-if="iapPrice('fluency')" class="iap-price">({{ iapPrice('fluency') }})</span>
          </button>
          <p v-if="paymentStrategy === 'stripe-preferred'" class="stripe-savings-note">
            {{ $t('pricing.stripeSavingsNote', 'Subscribe directly to save — no app store markup') }}
          </p>
        </template>
        <!-- Active subscribers: only offer the path their provider supports, so
             we never surface a button the backend dual-subscription guard rejects. -->
        <template v-else>
          <!-- Stripe subscriber: in-place upgrade via Stripe. -->
          <button v-if="providerView.showStripeUpgrade"
            @click="requestUpgrade(selectedBillingCycle === 'monthly' ? 'fluency-month' : 'fluency-year')"
            class="upgrade-btn" :disabled="isProcessing['fluency']">
            <span v-if="!isProcessing['fluency']">{{ $t('pricing.chooseFluency') }}</span>
            <span v-else class="loading-text">
              <font-awesome-icon icon="spinner" spin />
              {{ $t('pricing.processing') }}
            </span>
          </button>
          <!-- IAP subscriber on the matching native store: in-app upgrade. -->
          <button v-if="providerView.showIapUpgrade"
            @click="purchaseIap(selectedBillingCycle === 'monthly' ? 'fluency-monthly' : 'fluency-yearly', 'fluency')"
            class="upgrade-btn" :disabled="isProcessing['fluency']">
            <span v-if="!isProcessing['fluency']">
              {{ $t('pricing.chooseFluency') }}
              <span v-if="iapPrice('fluency')" class="iap-price">({{ iapPrice('fluency') }})</span>
            </span>
            <span v-else class="loading-text">
              <font-awesome-icon icon="spinner" spin />
              {{ $t('pricing.processing') }}
            </span>
          </button>
          <!-- Provider can't be used on this surface: guide to the right place. -->
          <p v-if="providerView.showGuide && providerView.guideKind === 'stripe'" class="provider-guide-note">
            {{ $t('pricing.manageOnWebNote', "You're subscribed with a card. Manage your plan on the ArticuLearn website.") }}
          </p>
          <p v-if="providerView.showGuide && providerView.guideKind === 'iap'" class="provider-guide-note">
            {{ $t('pricing.upgradeInAppNote', { store: providerStoreName }, `You're subscribed through ${providerStoreName}. Open the ArticuLearn app on that device to upgrade.`) }}
          </p>
        </template>
      </div>
    </div>

    <PlanLegalLinks />
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue';
import { pricingStore } from '@/store/pricing.js';
import { platformBilling } from '@/scripts/platformBilling.js';
import { iapUtils } from '@/scripts/iapUtils.js';
import PlanLegalLinks from '@/components/PlanLegalLinks.vue';

export default {
  name: 'UpgradeOptions',
  components: { PlanLegalLinks },
  props: {
    currentPlanId: {
      type: String,
      required: true, // e.g., 'free', 'foundation'
      default: 'free'
    },
    userCountry: {
      type: String,
      default: 'US'
    },
    // The provider of the user's current active subscription ('stripe' | 'apple'
    // | 'google'), or null when they are on the free tier. Drives provider-aware
    // upgrade options so an active subscriber is only offered the payment path
    // their provider supports.
    currentProvider: {
      type: String,
      default: null
    },
    // Async callback `(planType) => Promise<void>` that owns the real work of
    // starting a Stripe checkout. The button stays in its "Processing" state
    // until this promise resolves (browser opens / instant upgrade completes /
    // error thrown), replacing the old fixed 5s timer that re-enabled the
    // button mid-wait on native builds.
    onUpgrade: {
      type: Function,
      required: true
    },
    // Surfaces that upsell a Fluency-only capability (e.g. multi-language)
    // hide Foundation: buying it there would not unlock the feature.
    fluencyOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['iap-purchase-complete'],
  setup(props, { emit }) {
    const selectedBillingCycle = ref('monthly');
    const isProcessing = reactive({
      foundation: false,
      fluency: false
    });

    const paymentStrategy = computed(() => {
      return platformBilling.getPaymentStrategy(props.userCountry);
    });

    const storeName = computed(() => {
      return platformBilling.getStoreName();
    });

    // Which upgrade affordance an active subscriber may use on this surface.
    // For free users (currentProvider null) isLocked is false and the existing
    // region-based options render unchanged.
    const providerView = computed(() =>
      platformBilling.resolveUpgradeProviderView({
        currentProvider: props.currentProvider,
        paymentStrategy: paymentStrategy.value,
        isNativeApp: platformBilling.isNativeApp(),
        deviceStoreProvider: platformBilling.getDeviceStoreProvider(),
      })
    );

    // Store name keyed to the subscriber's provider (not the device) for the
    // IAP "open the app" guide message.
    const providerStoreName = computed(() =>
      platformBilling.getStoreNameForProvider(props.currentProvider)
    );

    const showFoundationPlan = computed(() => {
      return !props.fluencyOnly && props.currentPlanId === 'free';
    });

    const showFluencyPlan = computed(() => {
      return props.currentPlanId === 'free' || props.currentPlanId === 'foundation';
    });

    const requestUpgrade = async (planType) => {
      const planKey = planType.startsWith('foundation') ? 'foundation' : 'fluency';
      if (isProcessing[planKey]) return; // guard double-tap
      isProcessing[planKey] = true;
      try {
        await props.onUpgrade(planType);
      } finally {
        isProcessing[planKey] = false;
      }
    };

    const purchaseIap = async (productId, planKey) => {
      if (isProcessing[planKey]) return; // guard double-tap
      isProcessing[planKey] = true;
      try {
        const success = await iapUtils.purchase(productId);
        // Only a confirmed entitlement completes the purchase. Cancelled,
        // failed and still-pending orders all resolve false and have already
        // told the user what happened (iapUtils.purchase owns that messaging,
        // because only it knows which of the three it was).
        if (success) {
          emit('iap-purchase-complete', productId);
        }
      } catch (error) {
        console.error('IAP purchase failed:', error);
      } finally {
        isProcessing[planKey] = false;
      }
    };

    const iapPrice = (planName) => {
      const basePlanId = pricingStore.getBasePlanId(planName, selectedBillingCycle.value);
      if (!basePlanId) return null;
      return iapUtils.getProductPrice(basePlanId);
    };

    return {
      selectedBillingCycle,
      isProcessing,
      pricingStore,
      showFoundationPlan,
      showFluencyPlan,
      requestUpgrade,
      purchaseIap,
      paymentStrategy,
      storeName,
      iapPrice,
      providerView,
      providerStoreName,
    };
  }
};
</script>

<style scoped>
/* Styles copied and adapted from AccountView.vue and FreeChatView.vue */
.upgrade-options {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.billing-toggle {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 2rem;
}

.toggle-btn {
  padding: 8px 24px;
  border: none;
  border-radius: var(--al-radius-pill);
  background-color: var(--al-surface-sunken);
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.toggle-btn.active {
  background-color: var(--al-orange);
  /* White fails AA on orange (~2.6:1); ink-900 gives ~6.7:1, matching the
     .button.assessment precedent in LessonsView.vue */
  color: var(--al-ink-900);
}

.plans-container {
  display: grid;
  /* Adjust grid template columns for responsiveness */
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  justify-content: center;
  /* Center items if fewer than max fit */
}

.plan-card {
  background: var(--al-surface-raised);
  border-radius: var(--al-radius-lg);
  padding: 2rem;
  box-shadow: var(--al-shadow-md);
  transition: transform 0.3s ease;
  text-align: center;
  position: relative;
  /* Needed for absolute positioning of badge */
}

.plan-card:hover {
  transform: translateY(-5px);
}

/* Specific styling for fluency when upgrading from foundation */
.plan-card.fluency {
  border: 2px solid transparent;
  /* default border */
}

.plan-card.fluency .upgrade-highlight-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--al-orange);
  /* White fails AA on orange; ink-900 matches the .button.assessment precedent */
  color: var(--al-ink-900);
  padding: 5px 15px;
  border-radius: var(--al-radius-pill);
  font-size: 0.9rem;
  font-weight: bold;
  box-shadow: var(--al-shadow-sm);
}


/* Styles for Foundation card when it's the only upgrade option for Free users */
.plans-container:has(.plan-card.foundation) :not(:has(.plan-card.fluency)) .plan-card.foundation {
  /* Potentially add specific styles if needed when only foundation is shown */
}


.plan-card h3 {
  font-size: 1.5rem;
  color: var(--al-text);
  margin-bottom: 1rem;
}

.price-section {
  margin: 1.5rem 0;
  min-height: 120px;
  /* Ensure consistent height during transition */
}

.price-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.price {
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--al-text);
}

.per-month {
  color: var(--al-text-muted);
  margin-top: 0.25rem;
}

.yearly-savings {
  margin-top: 0.5rem;
  font-size: 0.9rem;
}

.billed-annually {
  color: var(--al-text-muted);
}

.months-free {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.highlight {
  /* Orange text on white is ~2.6:1 (fails AA); the primary-text role keeps
     the emphasis while the badge below carries the warm accent. */
  color: var(--al-text);
  font-weight: bold;
}

.savings-badge {
  background: var(--al-warm-soft);
  /* Orange text on warm-soft is ~2:1 (fails AA); the primary-text role gives
     ink on the light warm fill and near-white on the dark one. */
  color: var(--al-text);
  padding: 2px 8px;
  border-radius: var(--al-radius-pill);
  font-size: 0.8rem;
  margin-left: 0.5rem;
}

.plan-features-list {
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  text-align: left;
  /* Align feature text nicely */
}

.plan-features-list li {
  padding: 0.5rem 0;
  color: var(--al-text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Use a consistent checkmark or icon. Orange fails the 3:1 non-text contrast
   floor against white (~2.6:1); success-dark passes (~3.8:1) and matches the
   "green = complete" progress semantic for an included-feature marker. */
.plan-features-list li::before {
  content: "✓";
  color: var(--al-success-dark);
  font-weight: bold;
  margin-right: 0.5rem;
}


.upgrade-btn {
  width: 100%;
  min-height: 48px;
  padding: 1rem;
  border: none;
  border-radius: var(--al-radius-md);
  background-color: var(--al-orange);
  /* White fails AA on orange (~2.6:1); ink-900 matches the
     .button.assessment precedent (~6.7:1) */
  color: var(--al-ink-900);
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upgrade-btn:hover {
  /* Background stays --al-orange (no darker-orange token exists); the
     .button.assessment precedent signals hover with shadow instead. */
  box-shadow: var(--al-shadow-md);
}

.upgrade-btn:disabled {
  background-color: var(--al-border);
  cursor: not-allowed;
}

.upgrade-btn-secondary {
  width: 100%;
  min-height: 44px;
  padding: 0.625rem;
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-md);
  background-color: var(--al-surface-raised);
  color: var(--al-text-muted);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.upgrade-btn-secondary:hover {
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
  border-color: var(--al-border);
}

.upgrade-btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stripe-savings-note {
  text-align: center;
  color: var(--al-success-dark);
  font-size: 0.75rem;
  margin-top: 0.375rem;
  margin-bottom: 0;
  font-weight: 500;
}

.provider-guide-note {
  text-align: center;
  color: var(--al-text-muted);
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0.5rem 0 0;
}

.iap-price {
  font-weight: normal;
  font-size: 0.875rem;
  opacity: 0.9;
}

.loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

/* Price change animation */
.price-enter-active,
.price-leave-active {
  transition: all 0.3s ease;
}

.price-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.price-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.price-enter-to,
.price-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .upgrade-options {
    padding: 1rem;
    overflow-y: visible;
    max-height: none;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }

  .plans-container {
    grid-template-columns: 1fr;
    /* Stack cards on smaller screens */
    max-width: 350px;
    /* Limit width for better single-column layout */
    margin: 0 auto;
    /* Center the single column */
    padding-bottom: 1rem;
  }

  .plan-card {
    padding: 1.5rem;
    margin-bottom: 1rem;
  }

  .price {
    font-size: 2rem;
  }

  .toggle-btn {
    padding: 6px 18px;
    font-size: 14px;
  }

  /* Ensure upgrade highlight badge doesn't get cut off */
  .plan-card.fluency .upgrade-highlight-badge {
    top: -10px;
    font-size: 0.8rem;
    padding: 4px 12px;
  }

  /* Adjust billing toggle spacing */
  .billing-toggle {
    margin-bottom: 1.5rem;
    /* Remove sticky positioning on mobile */
    position: relative;
    padding: 0.5rem 0;
  }
}
</style>